import React, { useState, useEffect, useContext } from 'react';
import qs from 'qs';
import Spinner from '../../../components/react/spinner/spinner';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import { registerUser } from '../../services/user.api.service';
import { parseUserDataForApi } from '../../utilities/email_registration.utilities';
import './account_creation_for_email_registrations.scss';
import MembershipFlowContext from '../../context/membership_flow_context';
import { registrationTrackingEvent } from '../../../utilities/analytics';
import TermsAndConditions from '../../../components/react/terms_and_conditions/terms_and_conditions';
import { Redirect } from 'react-router-dom';

const specialCharactersRegex = /[!@#$%^&*(),?":{}|=+<>]/;

const AccountCreationForEmailRegistrations = () => {
  const { signupPage, paymentPage } = useContext(MembershipFlowContext);
  // Initializes useFormState with context values
  const [userFormData, setUserFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    orgSignupPageId: signupPage?.id,
    signupOrigin: `devex.com${document.location.pathname}?payment_page=${paymentPage}`,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const [isUserCreated, setIsUserCreated] = useState(false);
  const [showRequiredFirstName, setShowRequiredFirstName] = useState(false);
  const [showRequiredLastName, setShowRequiredLastName] = useState(false);
  const [showInvalidPassword, setShowInvalidPassword] = useState(false);
  const [showSpecialCharFirstNameError, setShowSpecialCharFirstNameError] = useState(false);
  const [showSpecialCharLastNameError, setShowSpecialCharLastNameError] = useState(false);

  const authTokenSelector = document.querySelector('meta[name="csrf-token"]');
  const authToken = authTokenSelector ? authTokenSelector.getAttribute('content') : null;
  // Extracts email from the url and attach its into the data to do the user creation request
  useEffect(() => {
    const search = window.location.search;
    if (search) {
      const incomingParams = qs.parse(search, { ignoreQueryPrefix: true });
      setUserFormData({
        ...userFormData,
        email: incomingParams.email,
      });
    }
  }, []);

  // Track user registration in Plausible if it has not been tracked yet
  useEffect(() => {
    registrationTrackingEvent();
  }, []);

  const submitForm = async (event) => {
    event.preventDefault();

    setShowRequiredFirstName(!userFormData.firstName.trim().length);
    setShowRequiredLastName(!userFormData.lastName.trim().length);
    setShowInvalidPassword(userFormData.password.length < 8);
    setShowSpecialCharFirstNameError(specialCharactersRegex.test(userFormData.firstName));
    setShowSpecialCharLastNameError(specialCharactersRegex.test(userFormData.lastName));
    if (
      !userFormData.firstName.trim().length ||
      !userFormData.lastName.trim().length ||
      userFormData.password.length < 8 ||
      specialCharactersRegex.test(userFormData.firstName) ||
      specialCharactersRegex.test(userFormData.lastName)
    )
      return;

    setIsLoading(true);

    try {
      await registerUser(parseUserDataForApi(userFormData), authToken);
      setIsUserCreated(true);
    } catch (error) {
      setIsLoading(false);
      setAlertData({ type: 'error', message: error.error });
    }
  };

  const renderRequiredFieldError = (
    <div className='mt-2 text-danger'>Please fill in this field to continue.</div>
  );

  const renderFirstNameSpecialCharError = (
    <div className='mt-2 text-danger'>Invalid character. Please enter a valid first name.</div>
  );

  const renderLastNameSpecialCharError = (
    <div className='mt-2 text-danger'>Invalid character. Please enter a valid last name.</div>
  );

  const renderInvalidPasswordError = (
    <div className='mt-2 text-danger'>Please use at least 8 characters.</div>
  );

  const weakPassword = userFormData.password.length < 8;
  const textColour = weakPassword ? 'text-muted' : 'text-success';
  const textIcon = weakPassword ? 'icon-times' : 'icon-check';

  const renderPasswordStrengthindicator = (
    <div className={`mt-2 ${textColour}`}>
      <i className={`fal small ${textIcon} me-2`} />At least 8 characters.
    </div>
  );

  const renderFirstNameInput = (
    <div className='form-group mb-3'>
      <label className='form-label label-required' htmlFor='first-name'>
        First name
      </label>
      <input
        id='first-name'
        data-testid='first-name-input'
        name='first-name'
        aria-label='First name input'
        className='form-control'
        type='text'
        required
        value={userFormData.firstName}
        onChange={(event) => {
          setUserFormData({
            ...userFormData,
            firstName: event.target.value,
          });
        }}
      ></input>
      {showRequiredFirstName && renderRequiredFieldError}
      {showSpecialCharFirstNameError && renderFirstNameSpecialCharError}
    </div>
  );

  const renderLastNameInput = (
    <div className='form-group mb-3'>
      <label className='form-label label-required' htmlFor='last-name'>
        Last name
      </label>
      <input
        id='last-name'
        data-testid='last-name-input'
        name='last-name'
        aria-label='Last name input'
        className='form-control'
        type='text'
        required
        value={userFormData.lastName}
        onChange={(event) => {
          setUserFormData({
            ...userFormData,
            lastName: event.target.value,
          });
        }}
      ></input>
      {showRequiredLastName && renderRequiredFieldError}
      {showSpecialCharLastNameError && renderLastNameSpecialCharError}
    </div>
  );

  const renderPasswordInput = (
    <div className='form-group mb-3'>
      <label className='label-required form-label' htmlFor='password'>
        Password
      </label>
      <input
        data-testid='password-input'
        id='password'
        name='password'
        aria-label='password'
        className='form-control'
        type='password'
        required
        value={userFormData.password}
        onChange={(event) => {
          setUserFormData({
            ...userFormData,
            password: event.target.value,
          });
        }}
        minLength={8}
      ></input>
      {showInvalidPassword ? renderInvalidPasswordError : renderPasswordStrengthindicator}
    </div>
  );

  const renderButton = (
    <button
      aria-label='create-user-button'
      type='submit'
      className='btn btn-dark w-100 btn-lg mt-3 mb-3'
    >
      <strong>Continue</strong>
    </button>
  );

  const renderUserAgreement = (
    <div className='mb-45'>
      <TermsAndConditions />
    </div>
  );

  if (isUserCreated) {
    const redirectTo = window.location.pathname.replace('/account-setup', '/your-details');
    return <Redirect push to={redirectTo} />;
  }

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div data-testid='account-setup' className='account-setup-step-global-styles'>
        <div className='title'>Let&apos;s set up your account</div>
        <form className='text-start' role='form' aria-label='form' noValidate onSubmit={submitForm}>
          {renderFirstNameInput}
          {renderLastNameInput}
          {renderPasswordInput}
          {renderUserAgreement}
          {renderButton}
        </form>
        <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
      </div>
    </>
  );
};

export default AccountCreationForEmailRegistrations;
